import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Fade,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  InsertDriveFileOutlined as FileIcon,
  Refresh as RetryIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getHeader, getToken } from "../utils/util";
import { API_ENDPOINT } from "../utils/constants";

// ─── constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 1500;
const STEPS = ["Select file", "Confirm", "Uploading & importing"];

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isExcelFile = (file) =>
  [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ].includes(file.type) ||
  file.name.endsWith(".xlsx") ||
  file.name.endsWith(".xls");

// ─── sub-components ───────────────────────────────────────────────────────────

const DropZone = ({ onFile, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <Box
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      sx={{
        border: "2px dashed",
        borderColor: dragging ? "primary.main" : "divider",
        borderRadius: 2,
        p: 5,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        bgcolor: dragging ? "action.hover" : "background.default",
        transition: "border-color 0.2s, background-color 0.2s",
        "&:hover": disabled
          ? {}
          : { borderColor: "primary.main", bgcolor: "action.hover" },
      }}
    >
      <UploadIcon
        sx={{
          fontSize: 44,
          color: dragging ? "primary.main" : "text.secondary",
          mb: 1,
        }}
      />
      <Typography variant="body1" fontWeight={500}>
        Drag &amp; drop your Excel file here
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        or click to browse &nbsp;·&nbsp; .xlsx and .xls supported
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = "";
        }}
      />
    </Box>
  );
};

const FilePill = ({ file, onRemove, disabled }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1.5}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      px: 2,
      py: 1.5,
      bgcolor: "background.paper",
    }}
  >
    <FileIcon color="primary" />
    <Box flex={1} minWidth={0}>
      <Typography variant="body2" fontWeight={500} noWrap>
        {file.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {fmtSize(file.size)}
      </Typography>
    </Box>
    {!disabled && (
      <IconButton size="small" onClick={onRemove} aria-label="Remove file">
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

const ProgressBlock = ({ uploadPct, job }) => {
  // Still uploading
  if (uploadPct !== null && uploadPct < 100) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" mb={0.75}>
          <Typography variant="body2" color="text.secondary">
            Uploading file…
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {uploadPct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={uploadPct}
          sx={{ borderRadius: 1, height: 8 }}
        />
      </Box>
    );
  }

  // File received — waiting for job to start
  if (!job || job.status === "starting" || job.status === "queued") {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" mb={0.75}>
          File uploaded — waiting for server to start…
        </Typography>
        <LinearProgress
          variant="indeterminate"
          sx={{ borderRadius: 1, height: 8 }}
        />
      </Box>
    );
  }

  // Job is processing (Validating or Importing)
  const pct = job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
  const statusText =
    job.status === "validating"
      ? "Validating contacts..."
      : "Importing contacts...";

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={0.75}>
        <Typography variant="body2" color="text.secondary">
          {statusText}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {job.progress.toLocaleString()} / {job.total.toLocaleString()}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ borderRadius: 1, height: 8 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        mt={0.5}
        display="block"
        textAlign="right"
      >
        {pct}% complete
      </Typography>
    </Box>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const ExcelImportDialog = ({
  open,
  onClose,
  url,
  statusBaseUrl = `${API_ENDPOINT}/user/import-job-status`,
  formFields = {},
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [job, setJob] = useState(null);
  const [done, setDone] = useState(false);

  const pollRef = useRef(null);
  const cancelRef = useRef(null);

  // ── teardown ────────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cancelUpload = useCallback(() => {
    if (cancelRef.current) {
      // FIX: AbortController uses .abort(), not .cancel()
      cancelRef.current.abort();
      cancelRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopPolling();
      cancelUpload();
    },
    [stopPolling, cancelUpload]
  );

  // ── reset ────────────────────────────────────────────────────────────────────

  const resetAll = useCallback(() => {
    stopPolling();
    cancelUpload();
    setActiveStep(0);
    setFile(null);
    setFileError(null);
    setSubmitError(null);
    setUploadPct(null);
    setJob(null);
    setDone(false);
  }, [stopPolling, cancelUpload]);

  useEffect(() => {
    if (open) resetAll();
  }, [open, resetAll]);

  // ── file selected ────────────────────────────────────────────────────────────

  const handleFile = useCallback((selectedFile) => {
    if (!isExcelFile(selectedFile)) {
      setFileError("Only .xlsx and .xls files are supported.");
      return;
    }
    setFileError(null);
    setSubmitError(null);
    setFile(selectedFile);
    setActiveStep(1);
  }, []);

  // ── upload & kick off job ────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setSubmitError(null);
    setUploadPct(0);
    setActiveStep(2);

    const controller = new AbortController();
    cancelRef.current = controller;

    try {
      const formData = new FormData();
      formData.append("file", file);

      Object.entries(formFields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const headers = {
        Authorization: `Bearer ${getToken()}`,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: headers,
        body: formData,
        signal: cancelRef.current.signal,
      });

      if (!res.ok) {
        let errorMessage = "Upload failed. Please try again.";
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Upload failed with status: ${res.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      setUploadPct(100);
      cancelRef.current = null;

      const { jobId } = data;
      // Initialize with the correct state
      setJob({ status: "starting", progress: 0, total: 0, error: null });
      startPolling(jobId);
    } catch (err) {
      if (err.name === "AbortError") return;

      const msg = err.message || "Upload failed. Please try again.";
      setSubmitError(msg);
      setUploadPct(null);
      setActiveStep(1);
    }
  };

  // ── polling ──────────────────────────────────────────────────────────────────

  const startPolling = useCallback(
    (id) => {
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${statusBaseUrl}/${id}`, getHeader());
          setJob(res.data);

          if (res.data.status === "done") {
            stopPolling();
            setDone(true);
          } else if (res.data.status === "failed") {
            stopPolling();
          }
        } catch {
          stopPolling();
          setJob((prev) => ({
            ...prev,
            status: "failed",
            error: "Lost connection while checking import status.",
          }));
        }
      }, POLL_INTERVAL_MS);
    },
    [statusBaseUrl, stopPolling]
  );

  // ── close guard ──────────────────────────────────────────────────────────────

  const handleClose = () => {
    // Guards against closing while busy
    const isImportingGuard =
      job && ["starting", "validating", "importing"].includes(job.status);
    const isUploadingGuard = uploadPct !== null && uploadPct < 100;

    if (isImportingGuard || isUploadingGuard) return;

    stopPolling();
    if (done && onSuccess) onSuccess();
    onClose();
  };

  // ── derived ──────────────────────────────────────────────────────────────────

  const isUploading = uploadPct !== null && uploadPct < 100;
  const isImporting =
    (uploadPct === 100 || uploadPct === null) &&
    job &&
    ["starting", "validating", "importing", "queued"].includes(job.status);

  const isBusy = isUploading || isImporting;
  const isFailed = job && job.status === "failed";
  const canClose = !isBusy;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={canClose ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Import contacts from Excel
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supported: .xlsx, .xls
          </Typography>
        </Box>
        {canClose && (
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <Divider />

      <Box px={3} pt={2}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {activeStep === 0 && (
          <Fade in>
            <Box>
              <DropZone onFile={handleFile} disabled={false} />
              <Collapse in={!!fileError}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  {fileError}
                </Alert>
              </Collapse>
            </Box>
          </Fade>
        )}

        {activeStep === 1 && (
          <Fade in>
            <Box display="flex" flexDirection="column" gap={2}>
              {file && <FilePill file={file} onRemove={resetAll} />}

              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={fmtSize(file?.size ?? 0)}
                  variant="outlined"
                />
                {Object.entries(formFields).map(([k, v]) => (
                  <Chip
                    key={k}
                    size="small"
                    label={`${k}: ${v}`}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>

              <Collapse in={!!submitError}>
                <Alert severity="error">{submitError}</Alert>
              </Collapse>

              <Alert severity="info" icon={false}>
                The file will be uploaded and processed on the server. You can
                track progress in the next step.
              </Alert>
            </Box>
          </Fade>
        )}

        {activeStep === 2 && (
          <Fade in>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              {(isBusy ||
                (job && ["queued", "starting"].includes(job.status))) && (
                <ProgressBlock uploadPct={uploadPct} job={job} />
              )}

              {done && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1.5}
                  py={2}
                >
                  <SuccessIcon sx={{ fontSize: 56, color: "success.main" }} />
                  <Typography variant="h6" fontWeight={600} textAlign="center">
                    Import complete!
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {(job?.total ?? 0).toLocaleString()} contact
                    {job?.total !== 1 ? "s" : ""} imported successfully.
                  </Typography>
                </Box>
              )}

              {isFailed && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1.5}
                  py={2}
                >
                  <ErrorIcon sx={{ fontSize: 56, color: "error.main" }} />
                  <Typography variant="h6" fontWeight={600} textAlign="center">
                    Import failed
                  </Typography>
                  <Alert severity="error" sx={{ width: "100%" }}>
                    {job?.error || "An unexpected error occurred."}
                  </Alert>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {isFailed && (
          <Button
            startIcon={<RetryIcon />}
            variant="outlined"
            onClick={resetAll}
            size="small"
          >
            Try again
          </Button>
        )}

        {(done || isFailed) && (
          <Button variant={done ? "contained" : "text"} onClick={handleClose}>
            {done ? "Done" : "Close"}
          </Button>
        )}

        {activeStep === 1 && (
          <>
            <Button variant="text" onClick={resetAll} sx={{ mr: "auto" }}>
              Choose different file
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!file}>
              Upload &amp; import
            </Button>
          </>
        )}

        {isBusy && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: "100%", textAlign: "center" }}
          >
            {isUploading
              ? "Uploading — please don't close this window…"
              : "Import running in background — please wait…"}
          </Typography>
        )}

        {activeStep === 0 && (
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportDialog;
